<template>
  <div class="settings-card weflix-admin-security">
    <header class="settings-card__header">
      <span class="settings-card__icon" aria-hidden="true">
        <fa icon="fa-shield-halved" />
      </span>
      <div>
        <h2 class="settings-card__title">Segurança</h2>
        <p class="settings-card__description">
          Defina o acesso de alunos e gestores, e quando um IP deve ser bloqueado por excesso de tentativas.
        </p>
      </div>
    </header>

    <div v-if="hasReady">
      <div class="security-tabs" role="tablist" aria-label="Papel">
        <button
          type="button"
          role="tab"
          class="security-tabs__tab"
          :class="{ 'is-active': securityRoleTab === 'student' }"
          :aria-selected="securityRoleTab === 'student'"
          @click="securityRoleTab = 'student'"
        >
          Aluno
        </button>
        <button
          type="button"
          role="tab"
          class="security-tabs__tab"
          :class="{ 'is-active': securityRoleTab === 'manager' }"
          :aria-selected="securityRoleTab === 'manager'"
          @click="securityRoleTab = 'manager'"
        >
          Gestor
        </button>
      </div>

      <section v-show="securityRoleTab === 'student'" class="security-role" role="tabpanel">
        <div class="security-list">
          <div class="security-row">
            <div>
              <strong>Login via e-mail</strong>
              <p>O aluno entra com um link enviado por e-mail, sem senha. Essa opção é independente da verificação em duas etapas.</p>
            </div>
            <div class="settings-option__controls">
              <span class="settings-badge" :class="emailLinkLogin ? 'is-on' : 'is-off'">
                {{ emailLinkLogin ? 'Ativo' : 'Inativo' }}
              </span>
              <div class="form-check form-switch">
                <input
                  id="setting-email-link-login"
                  class="form-check-input"
                  type="checkbox"
                  role="switch"
                  v-model="emailLinkLogin"
                  :disabled="saving"
                />
              </div>
            </div>
          </div>

          <div class="security-row">
            <div>
              <strong>2FA obrigatório</strong>
              <p>Por padrão fica desligado. Ativado, o login com senha exige o método abaixo. Desativado, o aluno entra só com a senha e pode ligar o 2FA só na conta dele. Ao desligar, o método volta para o código por e-mail.</p>
            </div>
            <div class="settings-option__controls">
              <span class="settings-badge" :class="studentTwoFactorRequired ? 'is-on' : 'is-off'">
                {{ studentTwoFactorRequired ? 'Ativo' : 'Inativo' }}
              </span>
              <div class="form-check form-switch">
                <input
                  id="setting-student-two-factor"
                  class="form-check-input"
                  type="checkbox"
                  role="switch"
                  :checked="studentTwoFactorRequired"
                  :disabled="saving"
                  @change="setStudentTwoFactor($event.target.checked)"
                />
              </div>
            </div>
          </div>
        </div>

        <fieldset v-if="studentTwoFactorRequired" class="security-methods">
          <legend>Forma de confirmação</legend>
          <p>Marque o aplicativo, o código por e-mail, ou os dois.</p>
          <label
            class="security-method"
            :class="{ 'is-selected': hasSecurityMethod(twoFactorMethod, 'authenticator') }"
          >
            <input
              type="checkbox"
              :checked="hasSecurityMethod(twoFactorMethod, 'authenticator')"
              :disabled="saving"
              @change="toggleStudentMethod('authenticator', $event.target.checked, $event)"
            />
            <span>
              <strong>Google Authenticator</strong>
              <small>O aluno escaneia um QR code no aplicativo Google Authenticator.</small>
            </span>
          </label>
          <label
            class="security-method"
            :class="{ 'is-selected': hasSecurityMethod(twoFactorMethod, 'email') }"
          >
            <input
              type="checkbox"
              :checked="hasSecurityMethod(twoFactorMethod, 'email')"
              :disabled="saving"
              @change="toggleStudentMethod('email', $event.target.checked, $event)"
            />
            <span>
              <strong>Código por e-mail</strong>
              <small>O aluno recebe um código de 6 dígitos por e-mail.</small>
            </span>
          </label>
        </fieldset>
      </section>

      <section v-show="securityRoleTab === 'manager'" class="security-role" role="tabpanel">
        <div class="security-list">
          <div class="security-row">
            <div>
              <strong>2FA obrigatório</strong>
              <p>A verificação em duas etapas do gestor fica sempre ativa.</p>
            </div>
            <div class="settings-option__controls is-locked">
              <span class="settings-badge is-on">Sempre ativo</span>
              <div class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked
                  disabled
                />
              </div>
            </div>
          </div>
        </div>

        <fieldset class="security-methods">
          <legend>Forma de confirmação</legend>
          <p>Marque o aplicativo, o código por e-mail, ou os dois.</p>
          <label
            class="security-method"
            :class="{ 'is-selected': hasSecurityMethod(managerTwoFactorMethod, 'authenticator') }"
          >
            <input
              type="checkbox"
              :checked="hasSecurityMethod(managerTwoFactorMethod, 'authenticator')"
              :disabled="saving"
              @change="toggleManagerMethod('authenticator', $event.target.checked, $event)"
            />
            <span>
              <strong>Google Authenticator</strong>
              <small>O gestor escaneia um QR code no aplicativo Google Authenticator.</small>
            </span>
          </label>
          <label
            class="security-method"
            :class="{ 'is-selected': hasSecurityMethod(managerTwoFactorMethod, 'email') }"
          >
            <input
              type="checkbox"
              :checked="hasSecurityMethod(managerTwoFactorMethod, 'email')"
              :disabled="saving"
              @change="toggleManagerMethod('email', $event.target.checked, $event)"
            />
            <span>
              <strong>Código por e-mail</strong>
              <small>O gestor recebe um código de 6 dígitos por e-mail.</small>
            </span>
          </label>
        </fieldset>
      </section>

      <div class="security-limit">
        <label for="loginAttemptLimit">Tentativas de login para bloquear o IP</label>
        <input
          id="loginAttemptLimit"
          type="number"
          min="1"
          step="1"
          v-model="loginAttemptLimit"
          placeholder="Ex.: 10"
          :disabled="saving"
        />
        <p>Depois desse número de tentativas do mesmo usuário, o IP deixa de conseguir entrar. Vazio não bloqueia.</p>
      </div>

      <div class="settings-card__actions">
        <button type="button" class="settings-save" :disabled="saving" @click="saveSecurity">
          {{ saving ? 'Salvando...' : 'Salvar alterações' }}
        </button>
      </div>
    </div>

    <p v-else class="security-loading">Carregando regras de segurança...</p>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { toast } from 'vue3-toastify';
import companySecurityApi, { securityToForm } from '@/utils/companySecurity';

export default {
  data() {
    return {
      hasReady: false,
      saving: false,
      securityRoleTab: 'student',
      studentTwoFactorRequired: false,
      emailLinkLogin: false,
      twoFactorMethod: 'email',
      managerTwoFactorMethod: 'email',
      loginAttemptLimit: '',
    };
  },
  computed: {
    ...mapState(['token']),
  },
  methods: {
    securityMethod(value) {
      if (value === 'authenticator' || value === 'both') return value;
      return 'email';
    },
    hasSecurityMethod(stored, method) {
      return stored === 'both' || stored === method;
    },
    nextSecurityMethod(stored, method, checked) {
      const email = method === 'email' ? checked : this.hasSecurityMethod(stored, 'email');
      const authenticator = method === 'authenticator'
        ? checked
        : this.hasSecurityMethod(stored, 'authenticator');
      if (email && authenticator) return 'both';
      if (authenticator) return 'authenticator';
      if (email) return 'email';
      return stored;
    },
    toggleStudentMethod(method, checked, event) {
      const next = this.nextSecurityMethod(this.twoFactorMethod, method, checked);
      if (next === this.twoFactorMethod && event?.target) {
        event.target.checked = this.hasSecurityMethod(this.twoFactorMethod, method);
      }
      this.twoFactorMethod = next;
    },
    toggleManagerMethod(method, checked, event) {
      const next = this.nextSecurityMethod(this.managerTwoFactorMethod, method, checked);
      if (next === this.managerTwoFactorMethod && event?.target) {
        event.target.checked = this.hasSecurityMethod(this.managerTwoFactorMethod, method);
      }
      this.managerTwoFactorMethod = next;
    },
    setStudentTwoFactor(enabled) {
      this.studentTwoFactorRequired = enabled;
      if (!enabled) this.twoFactorMethod = 'email';
    },
    applySecurity(security) {
      const next = securityToForm(security);
      this.studentTwoFactorRequired = next.studentTwoFactorRequired;
      this.emailLinkLogin = next.emailLinkLogin;
      this.twoFactorMethod = this.securityMethod(next.studentTwoFactorMethod || next.twoFactorMethod);
      this.managerTwoFactorMethod = this.securityMethod(next.managerTwoFactorMethod);
      this.loginAttemptLimit = next.loginAttemptLimit;
    },
    async loadSecurity() {
      try {
        const security = await companySecurityApi.get(this.token);
        this.applySecurity(security);
      } catch (error) {
        console.error('Falha ao carregar regras de segurança:', error);
        this.applySecurity(null);
        this.notify('Não foi possível carregar as regras de segurança.', 'error');
      } finally {
        this.hasReady = true;
      }
    },
    async saveSecurity() {
      const rawLimit = String(this.loginAttemptLimit ?? '').trim();
      if (rawLimit !== '' && !/^[1-9]\d*$/.test(rawLimit)) {
        this.notify('Informe um número inteiro maior que zero, ou deixe o campo vazio.', 'error');
        return;
      }
      this.saving = true;
      const studentMethod = this.studentTwoFactorRequired
        ? this.securityMethod(this.twoFactorMethod)
        : 'email';
      const managerMethod = this.securityMethod(this.managerTwoFactorMethod);
      try {
        const saved = await companySecurityApi.update(this.token, {
          studentTwoFactorRequired: this.studentTwoFactorRequired,
          studentTwoFactorMethod: studentMethod,
          managerTwoFactorRequired: true,
          managerTwoFactorMethod: managerMethod,
          emailLinkLogin: this.emailLinkLogin,
          twoFactorRequired: this.studentTwoFactorRequired,
          twoFactorMethod: studentMethod,
          loginAttemptLimit: rawLimit,
        });
        this.applySecurity(saved);
        this.notify('Configurações de segurança atualizadas com sucesso!', 'success');
      } catch (error) {
        console.error('Falha ao salvar regras de segurança:', error);
        this.notify('Erro ao atualizar as configurações de segurança.', 'error');
      } finally {
        this.saving = false;
      }
    },
    notify(message, type = 'info') {
      toast(message, {
        autoClose: 3000,
        position: 'top-right',
        theme: 'light',
        limit: 1,
        type,
        dangerouslyHTMLString: true,
      });
    },
  },
  created() {
    this.loadSecurity();
  },
};
</script>

<style scoped>
.settings-card {
  background: #fff;
  border: 1px solid #e7eaf2;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 3px 14px rgba(20, 25, 60, 0.03);
  text-align: left;
}

.settings-card__header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}

.settings-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f1eeff;
  color: #4327c9;
}

.settings-card__icon :deep(svg.svg-inline--fa) {
  color: #4327c9;
}

.settings-card__title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #11143d;
}

.settings-card__description {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
}

.security-tabs {
  display: inline-flex;
  gap: 4px;
  margin-bottom: 16px;
  padding: 4px;
  border-radius: 12px;
  background: #f3f4f8;
}

.security-tabs__tab {
  border: 0;
  border-radius: 9px;
  padding: 8px 16px;
  background: transparent;
  color: #5c6370;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.security-tabs__tab.is-active {
  background: #fff;
  color: #4327c9;
  box-shadow: 0 1px 2px rgba(67, 39, 201, 0.08);
}

.security-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.security-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border: 1px solid #e7eaf2;
  border-radius: 12px;
  background: #fafbfc;
}

.security-row strong {
  display: block;
  color: #11143d;
  font-size: 14px;
}

.security-row p,
.security-methods > p,
.security-limit p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.45;
}

.settings-option__controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-shrink: 0;
}

.settings-option__controls.is-locked {
  pointer-events: none;
}

.settings-badge {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 999px;
  white-space: nowrap;
  line-height: 1.2;
}

.settings-badge.is-on {
  background: #dcfce7;
  color: #166534;
}

.settings-badge.is-off {
  background: #fee2e2;
  color: #991b1b;
}

.settings-option__controls .form-check.form-switch {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 2.75rem;
  width: 2.75rem;
  min-height: 1.5rem;
  padding: 0 !important;
  margin: 0 !important;
}

.settings-option__controls .form-switch .form-check-input {
  float: none !important;
  width: 2.75rem !important;
  height: 1.5rem !important;
  margin: 0 !important;
  background-color: #e5e7eb;
  background-image: none !important;
  border: none;
  border-radius: 999px;
  position: relative;
  cursor: pointer;
  appearance: none;
}

.settings-option__controls .form-check-input:checked {
  background-color: #4327c9 !important;
}

.settings-option__controls .form-check-input:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.settings-option__controls .form-check-input::before {
  content: '';
  position: absolute;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background: #fff;
  top: 2px;
  left: 2px;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.settings-option__controls .form-check-input:checked::before {
  transform: translateX(1.25rem);
}

.security-methods {
  margin: 16px 0 0;
  padding: 0;
  border: 0;
  min-width: 0;
}

.security-methods legend {
  margin-bottom: 4px;
  color: #11143d;
  font-size: 14px;
  font-weight: 700;
}

.security-method {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 12px;
  padding: 16px;
  border: 1px solid #e7eaf2;
  border-radius: 12px;
  background: #fafbfc;
  cursor: pointer;
}

.security-method.is-selected {
  border-color: #4327c9;
  background: #f7f5fc;
}

.security-method input {
  margin-top: 3px;
  accent-color: #4327c9;
}

.security-method strong {
  display: block;
  color: #11143d;
  font-size: 14px;
}

.security-method small {
  display: block;
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.45;
}

.security-limit {
  margin-top: 16px;
  max-width: 360px;
}

.security-limit label {
  display: block;
  margin-bottom: 6px;
  color: #11143d;
  font-size: 14px;
  font-weight: 700;
}

.security-limit input {
  width: 100%;
  height: 42px;
  padding: 0 12px;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  background: #fff;
  color: #11143d;
  font-size: 14px;
}

.settings-card__actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
}

button.settings-save {
  height: 42px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  background: #4327c9;
  color: #fff !important;
  -webkit-text-fill-color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

button.settings-save:hover:not(:disabled) {
  background: #341bae;
}

button.settings-save:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.security-loading {
  margin: 8px 0 0;
  color: #64748b;
  font-size: 13px;
}

@media (max-width: 640px) {
  .settings-card {
    padding: 16px;
  }

  .security-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .settings-option__controls {
    align-self: flex-end;
  }

  .settings-save {
    width: 100%;
  }
}
</style>
